import { Check, Info, Minus } from "@icons";
import classNames from "classnames";
import { ReactNode, Ref, useEffect, useId, useRef, useState } from "react";

import { FieldSize } from "../_shared/field";
import FieldMessage from "../_shared/field-message";

export interface CheckboxProps {
  /** Text beside the box. */
  label?: ReactNode;
  /** Secondary muted line under the label. */
  description?: ReactNode;
  /** Description surfaced behind the (i) toggle next to the label. */
  info?: ReactNode;
  /** Start the description popover open. */
  infoOpen?: boolean;
  /** Accessible name for the input when there is no visible {@link label} (e.g. a table row-select). */
  ariaLabel?: string;
  /** Show a red required marker after the label. */
  required?: boolean;
  /** Controlled checked state (the parent owns it). */
  checked?: boolean;
  /** Dash state for partial selections — visual, overrides the check. */
  indeterminate?: boolean;
  /** Force the focus ring (controlled focus / showcase). */
  focused?: boolean;
  disabled?: boolean;
  /** Error message (red box + message row below). */
  error?: string;
  size?: FieldSize;
  /** Render as a selectable card that highlights when checked. */
  card?: boolean;
  name?: string;
  id?: string;
  /** Fires with the next checked value. */
  onChange?: (checked: boolean) => void;
  /** Notified when the control blurs (react-hook-form integration). */
  onBlur?: () => void;
  /** Ref to the underlying input — used by {@link ControlledCheckbox} to wire react-hook-form's field ref. */
  inputRef?: Ref<HTMLInputElement>;
}

export function Checkbox({
  label,
  description,
  info,
  infoOpen = false,
  ariaLabel,
  required = false,
  checked = false,
  indeterminate = false,
  focused = false,
  disabled = false,
  error,
  size = "md",
  card = false,
  name,
  id,
  onChange,
  onBlur,
  inputRef,
}: CheckboxProps) {
  const [showInfo, setShowInfo] = useState(infoOpen);
  const innerRef = useRef<HTMLInputElement | null>(null);
  const reactId = useId();
  const inputId = id ?? name ?? reactId;

  const hasError = !!error;
  const filled = checked || indeterminate;

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate, checked]);

  function assignRef(node: HTMLInputElement | null) {
    innerRef.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef)
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        node;
  }

  return (
    <div className="block w-full font-sans">
      <label
        htmlFor={inputId}
        className={classNames(
          "group relative flex",
          description ? "items-start" : "items-center",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          card
            ? classNames(
                "gap-3 w-auto px-[15px] py-[13px] rounded-lg border transition-[border-color,background,box-shadow]",
                {
                  "border-primary-300 bg-primary-50 shadow-md":
                    checked && !disabled,
                  "border-gray-200 bg-white hover:border-primary-200 hover:bg-[#f5f7fb]":
                    !checked && !disabled,
                  "border-gray-200 bg-white": disabled,
                },
              )
            : "gap-2.5 w-fit",
        )}
      >
        <input
          ref={assignRef}
          id={inputId}
          name={name}
          type="checkbox"
          aria-label={ariaLabel}
          className="peer absolute w-px h-px m-0 p-0 opacity-0 pointer-events-none"
          checked={checked}
          disabled={disabled}
          onChange={(event) => {
            if (!disabled) onChange?.(event.target.checked);
          }}
          onBlur={onBlur}
        />

        <span
          aria-hidden="true"
          className={classNames(
            "grid place-items-center flex-shrink-0 rounded-sm border-[1.5px] transition-[background,border-color,box-shadow]",
            size === "sm"
              ? "w-4 h-4"
              : size === "lg"
                ? "w-5 h-5"
                : "w-[18px] h-[18px]",
            description && "mt-px",
            {
              "bg-gray-100 border-gray-200 text-gray-400": disabled && !filled,
              "bg-gray-300 border-gray-300 text-white": disabled && filled,
              "bg-primary-500 border-primary-500 text-white group-hover:bg-primary-600 group-hover:border-primary-600":
                !disabled && filled,
              "bg-white border-gray-300 text-white group-hover:border-primary-400":
                !disabled && !filled && !hasError,
              "bg-white border-red-500 text-white":
                !disabled && !filled && hasError,
            },
            hasError
              ? "peer-focus-visible:ring-2 peer-focus-visible:ring-red-500/20"
              : "peer-focus-visible:border-primary-300 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-300/40",
            focused &&
              (hasError
                ? "ring-2 ring-red-500/20"
                : "border-primary-300 ring-2 ring-primary-300/40"),
          )}
        >
          <span
            className={classNames(
              "col-start-1 row-start-1 flex transition-[opacity,transform]",
              checked && !indeterminate
                ? "opacity-100 scale-100"
                : "opacity-0 scale-[.6]",
            )}
          >
            <Check size={size === "sm" ? 12 : 13} strokeWidth={3} />
          </span>
          <span
            className={classNames(
              "col-start-1 row-start-1 flex transition-[opacity,transform]",
              indeterminate ? "opacity-100 scale-100" : "opacity-0 scale-[.6]",
            )}
          >
            <Minus size={size === "sm" ? 12 : 14} strokeWidth={3} />
          </span>
        </span>

        {(label || description) && (
          <span className="flex flex-col gap-0.5 min-w-0">
            {label && (
              <span
                className={classNames(
                  "inline-flex items-center gap-1.5 font-medium leading-[1.35]",
                  size === "sm" ? "text-[13px]" : "text-sm",
                  disabled ? "text-gray-400" : "text-gray-800",
                )}
              >
                {label}
                {required && <span className="text-red-500">*</span>}
                {info && (
                  <span className="relative inline-flex">
                    <button
                      type="button"
                      aria-label={
                        typeof label === "string"
                          ? `About ${label}`
                          : "More information"
                      }
                      aria-expanded={showInfo}
                      onClick={(event) => {
                        event.preventDefault();
                        setShowInfo((open) => !open);
                      }}
                      className={classNames(
                        "grid place-items-center w-[18px] h-[18px] shrink-0 rounded-full border-0 p-0 cursor-pointer transition-colors",
                        showInfo
                          ? "text-primary-500 bg-primary-50"
                          : "text-gray-300 hover:text-primary-500 hover:bg-primary-50",
                      )}
                    >
                      <Info size={14} />
                    </button>
                    {showInfo && (
                      <span
                        role="tooltip"
                        className="absolute top-[calc(100%+8px)] -left-2 z-30 w-[244px] px-3 py-2.5 rounded-md border border-gray-200 bg-white shadow-lg text-[12.5px] font-normal leading-normal text-gray-600"
                      >
                        <span
                          aria-hidden="true"
                          className="absolute -top-[5px] left-3 w-[9px] h-[9px] rotate-45 border-l border-t border-gray-200 bg-white"
                        />
                        {info}
                      </span>
                    )}
                  </span>
                )}
              </span>
            )}
            {description && (
              <span
                className={classNames(
                  "text-[12.5px] leading-[1.45]",
                  disabled ? "text-gray-400" : "text-gray-500",
                )}
              >
                {description}
              </span>
            )}
          </span>
        )}
      </label>

      {hasError && (
        <div className="ml-7">
          <FieldMessage error={error} />
        </div>
      )}
    </div>
  );
}

export default Checkbox;
